import React, { useEffect, useState } from "react";
import L from "leaflet";
import { Marker, useMap, useMapEvents } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { trackProductEvent } from "../../analytics/trackEvent";
import demoBasins from "../../data/demoBasins.json";
import "../../styles/components/MapMarkers.css";

const fishSpotIcon = L.icon({
  iconUrl: "/map-icons/map-fishing-spot-icon.png",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -28],
});

const fishSpotLockedIcon = L.icon({
  iconUrl: "/map-icons/map-fishing-spot-icon - locked.png",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -28],
});

function getBasinIcon(zoom) {
  const size = zoom >= 9 ? 34 : zoom >= 7 ? 30 : 26;
  return L.icon({
    iconUrl: "/map-icons/map-basin-icon.png",
    iconSize: [size, size],
    iconAnchor: [Math.round(size / 2), size],
    popupAnchor: [0, -size + 4],
  });
}

function parseCoords(coordinates) {
  if (!coordinates) return null;

  const matches = String(coordinates).match(/(-?\d+(?:\.\d+)?)/g);
  if (!matches || matches.length < 2) return null;

  const lat = Number(matches[0]);
  const lng = Number(matches[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { lat, lng };
}

function ClickHandler({ onClick }) {
  useMapEvents({
    click(event) {
      if (typeof onClick === "function") {
        onClick(event.latlng.lat, event.latlng.lng);
      }
    },
  });

  return null;
}

export default function MapMarkers({
  posts = [],
  backendCatches = [],
  mapSpots = [],
  marker,
  onMapClick,
  onBasinClick,
  onSpotClick,
  viewer,
}) {
  const { t } = useTranslation();
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());

  useEffect(() => {
    const syncZoom = () => setZoom(map.getZoom());
    map.on("zoomend", syncZoom);
    return () => map.off("zoomend", syncZoom);
  }, [map]);

  const MIN_ZOOM_FOR_SPOTS = 6;
  const showSpots = zoom >= MIN_ZOOM_FOR_SPOTS;

  return (
    <>
      <ClickHandler onClick={onMapClick} />
      {marker && <Marker position={[marker.lat, marker.lng]} />}

      {showSpots &&
        demoBasins.map((basin) => (
          <Marker
            key={`basin-${basin.id}`}
            position={[basin.lat, basin.lng]}
            icon={getBasinIcon(zoom)}
            eventHandlers={{
              click: (event) => {
                if (event?.originalEvent) {
                  event.originalEvent.stopPropagation();
                }

                if (typeof onBasinClick === "function") {
                  onBasinClick(basin);
                }
              },
            }}
          />
        ))}

      {showSpots &&
        mapSpots.map((spot) => {
          const lat = Number(spot?.lat);
          const lng = Number(spot?.lng);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

          const isLockedForViewer = spot.isLocationPublic === false;

          return (
            <Marker
              key={`spot-${spot.id}`}
              position={[lat, lng]}
              icon={isLockedForViewer ? fishSpotLockedIcon : fishSpotIcon}
              eventHandlers={{
                click: () => {
                  const nearbyPosts = posts.filter((post) => {
                    const parsed = parseCoords(post?.catch?.coordinates);
                    if (!parsed) return false;

                    return (
                      Math.abs(parsed.lat - lat) < 0.002 &&
                      Math.abs(parsed.lng - lng) < 0.002
                    );
                  });

                  const seen = new Set();
                  const unique = nearbyPosts.filter((post) => {
                    if (seen.has(post.id)) return false;
                    seen.add(post.id);
                    return true;
                  });

                  if (typeof onSpotClick === "function") {
                    trackProductEvent("map_spot_clicked", {
                      featureArea: "map",
                      metadata: {
                        source: "map_spot_marker",
                        is_private: Boolean(isLockedForViewer),
                        has_reviews: unique.length > 0,
                      },
                    });
                    onSpotClick({
                      id: spot.id,
                      userId: spot.userId,
                      name: spot.location || "map_fishing_spot",
                      location: spot.location || "map_custom_location",
                      lat,
                      lng,
                      reviews: unique,
                      isPrivate: isLockedForViewer,
                    });
                  }
                },
              }}
            />
          );
        })}

      {showSpots &&
        backendCatches.map((catchItem, idx) => {
          const parsed = parseCoords(catchItem?.coordinates);
          if (!parsed) return null;

          const { lat, lng } = parsed;
          const ownerId = catchItem.userId;
          const ownerUsername = catchItem.user || catchItem.username;
          const viewerId = viewer?.id;
          const viewerUsername = viewer?.username;

          const isPrivate =
            catchItem?.isLocationPublic === false &&
            ((ownerId && viewerId && ownerId !== viewerId) ||
              (!ownerId && ownerUsername && viewerUsername && ownerUsername !== viewerUsername));

          if (isPrivate) return null;

          const isLockedForViewer = catchItem?.isLocationPublic === false;

          return (
            <Marker
              key={`catch-${catchItem.id || idx}`}
              position={[lat, lng]}
              icon={isLockedForViewer ? fishSpotLockedIcon : fishSpotIcon}
              eventHandlers={{
                click: () => {
                  if (typeof onSpotClick === "function") {
                    trackProductEvent("map_spot_clicked", {
                      featureArea: "map",
                      metadata: {
                        source: "catch_marker",
                        is_private: Boolean(isLockedForViewer),
                        has_reviews: true,
                      },
                    });
                    onSpotClick({
                      name: catchItem.fish || t("map.catch"),
                      location: catchItem.location || t("map.unknown"),
                      lat,
                      lng,
                      reviews: [
                        {
                          id: catchItem.id || "catch",
                          text: catchItem.fish || "",
                          image: catchItem.imageUrl
                            ? { url: catchItem.imageUrl, type: "image" }
                            : null,
                          rating: catchItem.rating || 0,
                          feedback: catchItem.feedback || "",
                          coordinates: catchItem.coordinates,
                          location: catchItem.location,
                          time: catchItem.time,
                          date: catchItem.date,
                          lure: catchItem.lure,
                          rod: catchItem.rod,
                          reel: catchItem.reel,
                          isLocationPublic: catchItem.isLocationPublic,
                        },
                      ],
                      isPrivate: isLockedForViewer,
                    });
                  }
                },
              }}
            />
          );
        })}
    </>
  );
}
